import React from "react";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Alert,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Block as BlockIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  Contacts as ContactIcon,
} from "@mui/icons-material";
import AppDialog from "../../shared/components/AppDialog";

interface RestrictionModalProps {
  open: boolean;
  onClose: () => void;
  message?: string;
  contactPerson?: string;
  zIndex?: number;
}

const RestrictionModal: React.FC<RestrictionModalProps> = ({
  open,
  onClose,
  message = "Para editar reservas comunicarse con Mariela Mendes Nunes",
  contactPerson = "Mariela Mendes Nunes",
  zIndex,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile} // Pantalla completa en móvil
      sx={zIndex ? { zIndex } : undefined}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          background: "linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%)",
          border: "2px solid #f44336",
          boxShadow: "0 10px 40px rgba(244, 67, 54, 0.3)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header con gradiente */}
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)",
          color: "white",
          py: isMobile ? 3 : 2,
          px: isMobile ? 2 : 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
          position: "relative",
        }}
      >
        <BlockIcon
          sx={{
            fontSize: isMobile ? 32 : 28,
            animation: "pulse 2s infinite",
          }}
        />
        <Box sx={{ flex: 1 }}>
          <Typography
            variant={isMobile ? "h5" : "h6"}
            component="div"
            fontWeight="bold"
            sx={{ mb: 0.5 }}
          >
            🚫 Acceso Restringido
          </Typography>
          <Typography
            variant="body2"
            sx={{ opacity: 0.9, fontSize: isMobile ? "0.9rem" : "0.8rem" }}
          >
            Permisos insuficientes
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: isMobile ? 3 : 4,
          textAlign: "center",
        }}
      >
        {/* Icono principal animado */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 3,
            animation: "bounce 2s infinite",
          }}
        >
          <Paper
            elevation={4}
            sx={{
              p: 3,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
              border: "3px solid #f44336",
            }}
          >
            <WarningIcon
              sx={{
                fontSize: isMobile ? 64 : 48,
                color: "#f44336",
              }}
            />
          </Paper>
        </Box>

        {/* Mensaje principal */}
        <Typography
          variant={isMobile ? "h6" : "h5"}
          gutterBottom
          fontWeight="bold"
          color="error"
          sx={{ mb: 2 }}
        >
          No tienes permisos para editar reservas
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            mb: 3,
            fontSize: isMobile ? "1rem" : "1.1rem",
            lineHeight: 1.6,
          }}
        >
          Tu cuenta tiene permisos limitados. Para realizar modificaciones en
          las reservas, necesitas contactar al administrador.
        </Typography>

        {/* Alert con información de contacto */}
        <Alert
          severity="info"
          icon={<ContactIcon />}
          sx={{
            mb: 2,
            textAlign: "left",
            "& .MuiAlert-message": {
              width: "100%",
            },
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Contacto para autorizaciones:
          </Typography>
          <Typography variant="body2">📞 {contactPerson}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            💡 {message}
          </Typography>
        </Alert>

        {/* Información adicional */}
        <Paper
          elevation={1}
          sx={{
            p: 2,
            bgcolor: "rgba(33, 150, 243, 0.1)",
            border: "1px solid rgba(33, 150, 243, 0.2)",
          }}
        >
          <Typography variant="body2" color="primary">
            <strong>Permisos actuales:</strong>
            <br />
            ✅ Crear nuevas reservas
            <br />
            ✅ Consultar información
            <br />
            ❌ Editar reservas existentes
            <br />❌ Eliminar reservas
          </Typography>
        </Paper>
      </DialogContent>

      <DialogActions
        sx={{
          p: isMobile ? 3 : 4,
          pt: 0,
          justifyContent: "center",
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          size={isMobile ? "large" : "medium"}
          sx={{
            borderRadius: 3,
            px: isMobile ? 4 : 3,
            py: isMobile ? 1.5 : 1,
            background: "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #d32f2f 0%, #c62828 100%)",
            },
            fontWeight: "bold",
            textTransform: "none",
            fontSize: isMobile ? "1.1rem" : "1rem",
          }}
        >
          Entendido
        </Button>
      </DialogActions>

      {/* Estilos de animación */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
          
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
            40% { transform: translateY(-10px); }
            60% { transform: translateY(-5px); }
          }
        `}
      </style>
    </AppDialog>
  );
};

export default RestrictionModal;
